import React, { useCallback, useEffect, useState } from 'react';
import { Alert, Box, Button, Dialog, DialogActions, DialogContent, DialogTitle, FormControlLabel, MenuItem, Stack, Switch, TextField, Typography } from '@mui/material';
import { apiErrorMessage, launchApi } from '../api';
import { help } from '../helpTexts';
import { cardSx, chipButtonSx, launchColors } from '../launchTheme';
import { TemplateDraft, TemplateLaunchPreview, TemplateUnitDraft } from '../types';
import SectionCard from '../ui/SectionCard';
import { StatusPill } from '../ui/StatusPill';
import TemplateLaunchSyncDialog from './TemplateLaunchSyncDialog';

const ProcessTemplateTab: React.FC = () => {
  const [templates, setTemplates] = useState<TemplateDraft[]>([]);
  const [editor, setEditor] = useState<TemplateDraft | null>(null);
  const [phaseKey, setPhaseKey] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [notice, setNotice] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [creating, setCreating] = useState(false);
  const [newName, setNewName] = useState('');
  const [sync, setSync] = useState<{ id: string; name: string; launches: TemplateLaunchPreview[] } | null>(null);

  const apply = useCallback((next: TemplateDraft[], preferId?: string) => {
    setTemplates(next);
    const chosen = next.find((template) => template.id === preferId) || next.find((template) => template.active) || next[0] || null;
    setEditor(chosen ? cloneTemplate(chosen) : null);
    setPhaseKey((current) => (chosen?.phases.some((phase) => phase.key === current) ? current : chosen?.phases[0]?.key || ''));
  }, []);

  const load = useCallback((preferId?: string) => {
    launchApi.templateState()
      .then((state) => apply(state.templates || [], preferId))
      .catch((requestError) => setError(apiErrorMessage(requestError, 'Mallen kunde inte hämtas.')));
  }, [apply]);

  useEffect(() => { load(); }, [load]);

  const save = async () => {
    if (!editor) return;
    setBusy(true);
    setError(null);
    try {
      const saved = await launchApi.saveTemplate({ ...editor, units: placeOwnUnits(editor.units) });
      setNotice(saved.active ? 'Mallen är sparad och används för nya lanseringar.' : 'Mallen är sparad.');
      load(saved.id);
      if (saved.status === 'published') {
        launchApi.templateLaunches(saved.id)
          .then((launches) => { if (launches.length) setSync({ id: saved.id, name: saved.name, launches }); })
          .catch((requestError) => setError(apiErrorMessage(requestError, 'Lanseringar som redan finns kunde inte hämtas.')));
      }
    } catch (requestError: any) {
      if (requestError?.response?.status === 409) {
        load(editor.id);
        setError('Någon annan hann ändra mallen. Den har laddats om.');
      } else {
        setError(apiErrorMessage(requestError, 'Mallen kunde inte sparas.'));
      }
    } finally {
      setBusy(false);
    }
  };

  const create = async () => {
    setBusy(true);
    setError(null);
    try {
      const created = await launchApi.createTemplate(newName.trim(), editor?.id || null);
      setCreating(false);
      setNewName('');
      setNotice('Mallen är skapad. Markera den som aktiv när nya lanseringar ska använda den.');
      load(created.id);
    } catch (requestError) {
      setError(apiErrorMessage(requestError, 'Mallen kunde inte skapas.'));
    } finally {
      setBusy(false);
    }
  };

  const activate = async (id: string) => {
    setBusy(true);
    setError(null);
    try {
      const state = await launchApi.activateTemplate(id);
      setNotice('Mallen är nu aktiv för nya lanseringar.');
      apply(state.templates || [], id);
    } catch (requestError) {
      setError(apiErrorMessage(requestError, 'Mallen kunde inte markeras som aktiv.'));
    } finally {
      setBusy(false);
    }
  };

  const addPhase = () => {
    if (!editor) return;
    const key = `custom-phase-${shortId()}`;
    setEditor({ ...editor, phases: [...editor.phases, { key, number: editor.phases.length + 1, sourceTitle: 'Ny fas', displayName: 'Ny fas' }] });
    setPhaseKey(key);
  };

  return (
    <Stack spacing={2}>
      {error && <Alert severity="error" onClose={() => setError(null)}>{error}</Alert>}
      {notice && <Alert severity="success" onClose={() => setNotice(null)}>{notice}</Alert>}
      <SectionCard title="Processmall" info={help.settingsTemplate} headingLevel="h3">
        <Stack spacing={1.5}>
          <Typography sx={{ color: launchColors.textSecondary }}>
            Den aktiva mallen används när en ny lansering skapas. Spara skriver över den valda mallen. Lanseringar som redan finns behåller sin plan. Namn på rader och faser, roller och radernas ordning kan du föra över till dem när du sparar.
          </Typography>
          {templates.length === 0 && (
            <Stack spacing={1.5} alignItems="flex-start">
              <Typography sx={{ color: launchColors.textSecondary }}>Ingen mall är sparad. Nya lanseringar använder källfilen tills en mall markeras som aktiv.</Typography>
              <TextField label="Mallnamn" value={newName} onChange={(event) => setNewName(event.target.value)} />
              <Button variant="contained" disabled={busy || !newName.trim()} onClick={() => { void create(); }}>Skapa mall</Button>
            </Stack>
          )}
          {templates.length > 0 && (
            <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: 'repeat(2, minmax(0, 1fr))', md: 'repeat(3, minmax(0, 1fr))' }, gap: 1 }}>
              {templates.map((template) => {
                const selected = editor?.id === template.id;
                return (
                  <Box key={template.id} sx={[cardSx, { p: 1.5, bgcolor: template.active ? launchColors.petrolLight : launchColors.card, border: selected ? `2px solid ${launchColors.navy}` : `1px solid ${launchColors.divider}` }]}>
                    <Stack spacing={1}>
                      <Box
                        component="button"
                        type="button"
                        onClick={() => { setEditor(cloneTemplate(template)); setPhaseKey(template.phases[0]?.key || ''); }}
                        aria-pressed={selected}
                        sx={{ border: 0, bgcolor: 'transparent', p: 0, textAlign: 'left', cursor: 'pointer', font: 'inherit', color: 'inherit' }}
                      >
                        <Typography sx={{ fontSize: 16, fontWeight: 700, color: launchColors.navy }}>{template.name}</Typography>
                        <Typography sx={{ fontSize: 13, color: launchColors.textSecondary }}>
                          {template.active ? 'Används för nya lanseringar' : 'Används inte för nya lanseringar'}
                        </Typography>
                      </Box>
                      {template.active
                        ? <StatusPill label="Aktiv" fg={launchColors.mossDark} bg={launchColors.mossLight} />
                        : template.status === 'published' && <Button size="small" disabled={busy} onClick={() => { void activate(template.id); }}>Gör aktiv</Button>}
                    </Stack>
                  </Box>
                );
              })}
            </Box>
          )}
          {templates.length > 0 && <Button sx={{ alignSelf: 'flex-start' }} onClick={() => { setNewName(''); setCreating(true); }}>Ny mall</Button>}
        </Stack>
      </SectionCard>
      {editor && (
        <SectionCard
          title={editor.name || 'Mall'}
          headingLevel="h3"
          action={<Button variant="contained" disabled={busy || !editor.name.trim()} onClick={() => { void save(); }}>Spara mallen</Button>}
        >
          <Stack spacing={1.5}>
            <TextField label="Mallnamn" value={editor.name} onChange={(event) => setEditor({ ...editor, name: event.target.value })} />
            <Stack direction="row" spacing={1} useFlexGap flexWrap="wrap">
              {editor.phases.map((phase) => (
                <Button key={phase.key} onClick={() => setPhaseKey(phase.key)} sx={chipButtonSx(phase.key === phaseKey)}>{phase.displayName}</Button>
              ))}
              <Button onClick={addPhase} sx={chipButtonSx(false)}>Ny fas</Button>
            </Stack>
            {editor.phases.some((phase) => phase.key === phaseKey && phase.key.startsWith('custom-phase')) && (
              <TextField
                label="Fasnamn"
                value={editor.phases.find((phase) => phase.key === phaseKey)?.displayName || ''}
                onChange={(event) => setEditor({
                  ...editor,
                  phases: editor.phases.map((phase) => phase.key === phaseKey ? { ...phase, displayName: event.target.value, sourceTitle: event.target.value } : phase),
                })}
              />
            )}
            {editor.units.filter((unit) => unit.phaseKey === phaseKey).map((unit) => (
              <Stack key={unit.sourceKey} spacing={1} sx={{ p: 1.5, border: `1px solid ${launchColors.divider}`, borderRadius: '12px', bgcolor: launchColors.card }}>
                <Typography sx={{ fontSize: 12, fontWeight: 700, letterSpacing: '0.06em', textTransform: 'uppercase', color: launchColors.textSecondary }}>
                  {unit.sourceKey.startsWith('custom-') ? 'Ny' : 'Rad'}
                </Typography>
                <TextField label="Namn" value={unit.sourceTitle} onChange={(event) => updateUnit(setEditor, unit.sourceKey, { sourceTitle: event.target.value })} />
                <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1}>
                  <TextField label="Roll" value={unit.sourceOwner} onChange={(event) => updateUnit(setEditor, unit.sourceKey, { sourceOwner: event.target.value })} />
                  <TextField
                    type="number"
                    label="Veckor från avisering"
                    helperText="0 är aviseringsveckan, −6 är sex veckor före."
                    value={unit.offsetWeeks ?? ''}
                    onChange={(event) => updateUnit(setEditor, unit.sourceKey, { offsetWeeks: event.target.value === '' ? null : Number(event.target.value) })}
                  />
                  <TextField type="number" label="Vikt i framdrift" value={unit.weight} onChange={(event) => updateUnit(setEditor, unit.sourceKey, { weight: Number(event.target.value) })} />
                  <TextField select label="Typ" value={unit.role} onChange={(event) => updateUnit(setEditor, unit.sourceKey, { role: event.target.value as TemplateUnitDraft['role'], ...(event.target.value === 'calendar_marker' ? { weight: 0 } : {}) })}>
                    <MenuItem value="work">Arbete</MenuItem>
                    <MenuItem value="approval">Beslut</MenuItem>
                    <MenuItem value="calendar_marker">Kalenderhållpunkt</MenuItem>
                  </TextField>
                </Stack>
                <Stack direction="row" alignItems="center" justifyContent="space-between">
                  <FormControlLabel
                    control={<Switch checked={unit.excluded !== true} onChange={(event) => updateUnit(setEditor, unit.sourceKey, { excluded: !event.target.checked })} />}
                    label="Ingår"
                  />
                  {unit.sourceKey.startsWith('custom-') && (
                    <Button onClick={() => setEditor((current) => current ? { ...current, units: current.units.filter((item) => item.sourceKey !== unit.sourceKey) } : current)}>Ta bort rad</Button>
                  )}
                </Stack>
              </Stack>
            ))}
            <Stack direction="row" spacing={1.5} alignItems="center">
              <Button onClick={() => setEditor({ ...editor, units: [...editor.units, newRow(phaseKey)] })}>Ny rad</Button>
              <Typography sx={{ fontSize: 12, color: launchColors.textSecondary }}>Egna rader flyttas till rätt plats efter veckor från avisering när du sparar mallen.</Typography>
            </Stack>
          </Stack>
        </SectionCard>
      )}
      <Dialog open={creating} onClose={() => setCreating(false)} fullWidth maxWidth="xs">
        <DialogTitle>Ny mall</DialogTitle>
        <DialogContent>
          <TextField autoFocus fullWidth label="Mallnamn" value={newName} onChange={(event) => setNewName(event.target.value)} sx={{ mt: 1 }} helperText="Kopieras från den valda mallen och blir inte aktiv." />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setCreating(false)}>Avbryt</Button>
          <Button variant="contained" disabled={busy || !newName.trim()} onClick={() => { void create(); }}>Skapa</Button>
        </DialogActions>
      </Dialog>
      {sync && (
        <TemplateLaunchSyncDialog
          templateId={sync.id}
          templateName={sync.name}
          launches={sync.launches}
          onClose={() => setSync(null)}
          onDone={(doneNotice, doneError) => {
            setSync(null);
            if (doneNotice) setNotice(doneNotice);
            setError(doneError);
          }}
        />
      )}
    </Stack>
  );
};

function cloneTemplate(template: TemplateDraft): TemplateDraft {
  return JSON.parse(JSON.stringify(template)) as TemplateDraft;
}

function updateUnit(setEditor: React.Dispatch<React.SetStateAction<TemplateDraft | null>>, sourceKey: string, patch: Partial<TemplateUnitDraft>) {
  setEditor((current) => current ? { ...current, units: current.units.map((unit) => unit.sourceKey === sourceKey ? { ...unit, ...patch } : unit) } : current);
}

function newRow(phaseKey: string): TemplateUnitDraft {
  return {
    sourceKey: `custom-${shortId()}`,
    phaseKey,
    sourceTitle: 'Ny rad',
    sourceOwner: '',
    sourceTiming: '',
    offsetWeeks: 0,
    timingKind: 'relative_to_announcement',
    role: 'work',
    weight: 1,
    scopeProposal: 'included',
    completionPolicy: '',
    reviewState: 'unreviewed',
  };
}

function placeOwnUnits(units: TemplateUnitDraft[]): TemplateUnitDraft[] {
  const moving = units.filter((unit) => unit.sourceKey.startsWith('custom-') && unit.offsetWeeks != null);
  const result = units.filter((unit) => !moving.includes(unit));
  for (const unit of moving) {
    const weeks = unit.offsetWeeks as number;
    const inPhase = result.map((item, index) => ({ item, index })).filter(({ item }) => item.phaseKey === unit.phaseKey);
    const timed = inPhase.filter(({ item }) => item.offsetWeeks != null);
    const before = timed.filter(({ item }) => (item.offsetWeeks as number) <= weeks).pop();
    const index = before ? before.index + 1
      : timed.length ? timed[0].index
        : inPhase.length ? inPhase[inPhase.length - 1].index + 1
          : units.indexOf(unit);
    result.splice(Math.min(index, result.length), 0, unit);
  }
  return result;
}

function shortId(): string {
  return typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function'
    ? crypto.randomUUID().replace(/-/g, '').slice(0, 12)
    : String(Date.now());
}

export default ProcessTemplateTab;
